import { useLazyQuery } from '@apollo/client/react';
import { usePitchDeckUpload } from '@src/components/sco/PitchDeckAuditor/hooks/usePitchDeckUpload';
import { PitchDeckAuditAdditionalFields } from '@src/components/sco/RequestFunnel/PitchDeckAudit/types';
import { useRequestFunnelContext } from '@src/components/sco/RequestFunnel/RequestFunnelContext';
import { gql } from '@src/lib/gql';
import { prettyErrorMessage } from '@src/lib/mutationErrors';
import tracker from '@src/lib/tracker';
import { useRouter } from 'next/router';
import { useCallback, useState } from 'react';
const GROUP_REQUEST_PITCH_DECK_QUERY = gql`
  query PitchDeckDiagnosticGroupRequest($groupRequestId: String!) {
    groupRequest(groupRequestId: $groupRequestId) {
      id
      groupRequestId
      pitchDeckId
      pitchDeck {
        id
        pitchDeckId
        deckStoredFileId
      }
    }
  }
`;

/**
 * Commits the landing-diagnostic SignupRequest (creates GroupRequest + PitchDeck)
 * and opens the live auditor at `/pitch-decks/{id}`. Shared by the upload CTA
 * (logged-in users) and the register step (guests, after signup).
 */
export function useCommitPitchDeckDiagnostic() {
  const router = useRouter();
  const {
    isSaving,
    saveSignupRequest,
    signupRequest
  } = useRequestFunnelContext();
  const {
    createDeckFromStoredFile,
    replaceDeckFile
  } = usePitchDeckUpload();
  const [fetchGroupRequest] = useLazyQuery(GROUP_REQUEST_PITCH_DECK_QUERY, {
    fetchPolicy: 'network-only'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const commitAndOpenDeck = useCallback(async () => {
    setError(null);
    setIsSubmitting(true);
    try {
      await saveSignupRequest(sr => {
        void (async () => {
          try {
            const storedFileId = (sr?.additionalFields as PitchDeckAuditAdditionalFields | undefined)?.deckStoredFileId || (signupRequest.additionalFields as PitchDeckAuditAdditionalFields | undefined)?.deckStoredFileId;
            let pitchDeckId: string | null = null;
            if (sr?.groupRequestId) {
              const {
                data
              } = await fetchGroupRequest({
                variables: {
                  groupRequestId: sr.groupRequestId
                }
              });
              const groupRequest = data?.groupRequest;
              pitchDeckId = groupRequest?.pitchDeck?.pitchDeckId || groupRequest?.pitchDeckId || null;
              const hasFile = !!groupRequest?.pitchDeck?.deckStoredFileId;
              if (pitchDeckId && !hasFile && storedFileId) {
                await replaceDeckFile(pitchDeckId, storedFileId, {
                  analyze: true
                });
              }
            }

            // Commit may have created an empty shell if prefill missed the file.
            if (!pitchDeckId && storedFileId) {
              pitchDeckId = await createDeckFromStoredFile(storedFileId, {
                analyze: true
              });
            }
            if (!pitchDeckId) {
              setError('We could not start your audit. Please try again.');
              setIsSubmitting(false);
              return;
            }
            tracker.funnelStep(6);
            router.replace(`/pitch-decks/${pitchDeckId}`);
          } catch (e_0: unknown) {
            setError(prettyErrorMessage(e_0) || 'We could not start your audit. Please try again.');
            setIsSubmitting(false);
          }
        })();
      }, {
        commitToGroupRequest: true,
        additionalFields: signupRequest.additionalFields,
        pitchDeckUrl: signupRequest.pitchDeckUrl
      }, true);
    } catch (e: unknown) {
      setError(prettyErrorMessage(e) || 'Something went wrong. Please try again.');
      setIsSubmitting(false);
    }
  }, [createDeckFromStoredFile, fetchGroupRequest, replaceDeckFile, router, saveSignupRequest, signupRequest.additionalFields, signupRequest.pitchDeckUrl]);
  return {
    busy: isSaving || isSubmitting,
    commitAndOpenDeck,
    error,
    isSaving,
    isSubmitting
  };
}